import { useCallback, useEffect, useState } from 'react';
import { fetcher } from '../../components/Hooks/useListActions';
import EmphasisCatalogSection from '../../components/Site/EmphasisCatalogSection';
import EmphasisMediasSectionLoading from '../../components/Site/EmphasisMediasSectionLoading';
import { SectionProps } from '../../components/Site/Section';
import LangEnum from '../../lang/LangEnum';
import snackbarStore from '../../stores/Snackbar.store';
import { Catalog, EmphasisCatalog, EmphasisCatalogSimple } from '../catalogs/catalog.types';
import { useAuthContext } from '../login/AuthContext';
import { useTranslation } from '../translation/TranslationContext';
import EmphasisCatalogEdit from './EmphasisCatalogEdit';
import { brandCatalogShowcaseRepository } from './brandCatalogShowcase.repository';

type BrandEmphasisCatalogProps = {
  sectionProps: Partial<SectionProps>;
  brandId: string;
  enableLink: boolean;
};

const catalogsFetcher = fetcher<Catalog>('/catalogs');

const CATALOGS_LOAD_DELAY_MS = 500;

const CATALOGS_PAGE_SIZE = 999;

const BrandEmphasisCatalog = ({ sectionProps, brandId, enableLink }: BrandEmphasisCatalogProps) => {
  const { loggedin } = useAuthContext();
  const { handleMessage } = snackbarStore;
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [emphasisCatalogs, setEmphasisCatalogs] = useState<EmphasisCatalog[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { translate } = useTranslation();

  const loadBrandCatalogs = useCallback(async () => {
    const catalogsResponse = await catalogsFetcher({
      page: 0,
      count: CATALOGS_PAGE_SIZE,
    });
    if (catalogsResponse.tag === 'success') {
      setCatalogs(catalogsResponse.data);
    }
  }, [setCatalogs]);

  const loadBrandEmphasisCatalogs = useCallback(async () => {
    setIsLoadingCatalogs(true);
    const catalogsResponse = await brandCatalogShowcaseRepository(brandId, loggedin).list();
    if (catalogsResponse.ok) {
      setEmphasisCatalogs(catalogsResponse?.data?.data || []);
    }
    setIsLoadingCatalogs(false);
  }, [brandId, loggedin]);

  useEffect(() => {
    const emphasisCatalogsTimeout = setTimeout(loadBrandEmphasisCatalogs, CATALOGS_LOAD_DELAY_MS);

    return () => clearTimeout(emphasisCatalogsTimeout);
  }, [brandId, loadBrandEmphasisCatalogs]);

  useEffect(() => {
    let catalogsTimeout: NodeJS.Timeout | null = null;
    if (brandId && isEditing) {
      catalogsTimeout = setTimeout(loadBrandCatalogs, CATALOGS_LOAD_DELAY_MS);
    } else {
      setCatalogs([]);
    }

    return () => {
      if (catalogsTimeout) {
        clearTimeout(catalogsTimeout);
      }
    };
  }, [isEditing, brandId, loadBrandCatalogs]);

  const handleSaveEmphasisCatalog = async (emphasisCatalog: EmphasisCatalog[]) => {
    setIsSaving(true);
    const emphasisCatalogsSimple: EmphasisCatalogSimple[] = emphasisCatalog.map((ec, index) => ({
      catalogId: ec.catalog.id,
      position: index,
    }));

    const result =
      await brandCatalogShowcaseRepository(brandId).synchronize(emphasisCatalogsSimple);

    if (result.ok) {
      await loadBrandEmphasisCatalogs();
      setIsEditing(false);
    } else {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.SHOWROOM_FEATURED_CATALOGS_ERROR),
      });
    }
    setIsSaving(false);
  };

  if (sectionProps.readOnly && emphasisCatalogs.length === 0) {
    return null;
  }

  return (
    <>
      {!sectionProps.readOnly && (
        <EmphasisCatalogEdit
          isOpen={isEditing}
          isSaving={isSaving}
          catalogs={catalogs}
          emphasisCatalogs={emphasisCatalogs}
          onSave={handleSaveEmphasisCatalog}
          onClose={() => setIsEditing(false)}
        />
      )}
      {isLoadingCatalogs ? (
        <EmphasisMediasSectionLoading />
      ) : (
        <EmphasisCatalogSection
          sectionProps={{ ...sectionProps, onEdit: () => setIsEditing(true) }}
          emphasisCatalogs={emphasisCatalogs}
          enableLink={enableLink}
        />
      )}
    </>
  );
};

export default BrandEmphasisCatalog;
